import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { usePopper } from "react-popper";
import styles from "./Tooltip.module.scss";

/**
 * This constant exports not in separate file
 * because storybook can't read propTypes
 * for displaying in prop table  */
export const POSITIONS = ["top", "bottom", "right", "left"];
export const ALIGNS = ["start", "end"];
const SHOW_EVENTS = ["mouseenter", "focus"];
const HIDE_EVENTS = ["mouseleave", "blur"];

const setPlacement = (position, align) => {
  const alignment = align ? `-${align}` : "";
  return `${position || "auto"}${alignment}`;
};

const defaultOptions = [{ name: "offset", options: { offset: [0, 8] } }];

const Tooltip = ({ children, content, position, align, options, className }) => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const { styles: PopperStyles, attributes } = usePopper(referenceElement, popperElement, {
    placement: setPlacement(position, align),
    modifiers: [{ name: "arrow", options: { element: arrowElement } }, ...defaultOptions, ...options]
  });

  const show = useCallback(() => popperElement.setAttribute("data-show", ""), [popperElement]);
  const hide = useCallback(() => {
    if (document.activeElement !== referenceElement) {
      popperElement.removeAttribute("data-show");
    }
  }, [popperElement, referenceElement]);

  useEffect(() => {
    if (!referenceElement) {
      return () => {};
    }

    SHOW_EVENTS.forEach(event => {
      referenceElement.addEventListener(event, show);
    });

    HIDE_EVENTS.forEach(event => {
      referenceElement.addEventListener(event, hide);
    });

    return () => {
      SHOW_EVENTS.forEach(event => {
        referenceElement.removeEventListener(event, show);
      });

      HIDE_EVENTS.forEach(event => {
        referenceElement.removeEventListener(event, hide);
      });
    };
  }, [referenceElement, show, hide]);

  return (
    <span className={cx(styles.container, className)}>
      <span tabIndex={-1} ref={setReferenceElement} className={styles["reference-element"]}>
        {children}
      </span>
      <div ref={setPopperElement} className={cx(styles.tooltip)} style={PopperStyles.popper} {...attributes.popper}>
        {content}
        <div ref={setArrowElement} className={styles.arrow} style={PopperStyles.arrow} />
      </div>
    </span>
  );
};

export default Tooltip;

Tooltip.defaultProps = {
  position: null,
  align: null,
  options: [{}],
  className: ""
};

Tooltip.propTypes = {
  /** The element that will trigger tooltip element. */
  children: PropTypes.node.isRequired,
  /** The actual tooltip content. */
  content: PropTypes.node.isRequired,
  /** The positions for the tooltip element. */
  position: PropTypes.oneOf(POSITIONS),
  /** The alignments for the tooltip element. */
  align: PropTypes.oneOf(ALIGNS),
  /** Options for popper. */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      options: PropTypes.object
    })
  ),
  /** ClassNames for overriding default behaviours. */
  className: PropTypes.string
};
